import React, { useEffect, useState } from "react";
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  format,
  getDay,
  startOfMonth,
  subMonths,
} from "date-fns";
import { fr } from "date-fns/locale";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import FlipClock from "./FlipClock";
import "../../styles/ClockCalendar.css";

const daysOfWeek = ["L", "M", "M", "J", "V", "S", "D"];

const ClockCalendar = ({ className = "" }) => {
  const [time, setTime] = useState(new Date());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(interval);
  }, []);

  const handlePrevMonth = () => {
    setDirection(-1);
    setCurrentDate((previousDate) => subMonths(previousDate, 1));
  };

  const handleNextMonth = () => {
    setDirection(1);
    setCurrentDate((previousDate) => addMonths(previousDate, 1));
  };

  const getTimeInZone = (utcOffset) => {
    const utcTime = new Date(time.getTime() + time.getTimezoneOffset() * 60000);
    return format(new Date(utcTime.getTime() + utcOffset * 3600000), "HH:mm");
  };

  const currentMonth = format(currentDate, "MMMM yyyy", { locale: fr }).toUpperCase();
  const today = format(new Date(), "yyyy-MM-dd");
  const monthDays = eachDayOfInterval({
    start: startOfMonth(currentDate),
    end: endOfMonth(currentDate),
  });
  const leadingEmptyDays = (getDay(monthDays[0]) + 6) % 7;

  return (
    <div
      className={`clock-calendar bg-surface shadow-hp-md rounded-lg ${className}`.trim()}
    >
      <div className="clock-calendar__main">
        <section className="clock-calendar__local-time" aria-label="Heure locale">
          <div className="clock-calendar__time">{format(time, "HH:mm")}</div>
          <div className="clock-calendar__date">
            {format(time, "EEEE d MMMM", { locale: fr })}
          </div>
        </section>

        <div className="clock-calendar__divider" aria-hidden="true" />

        <section className="clock-calendar__month" aria-label="Calendrier mensuel">
          <div className="clock-calendar__month-navigation">
            <button
              onClick={handlePrevMonth}
              aria-label="Mois précédent"
              className="clock-calendar__navigation-button p-1 flex items-center justify-center rounded-full hover:bg-surface-muted transition-colors text-primary"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>

            <AnimatePresence mode="wait">
              <motion.h2
                key={currentMonth}
                initial={{ opacity: 0, x: direction === 1 ? 10 : -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: direction === 1 ? -10 : 10 }}
                transition={{ duration: 0.3 }}
                className="clock-calendar__month-title"
              >
                {currentMonth}
              </motion.h2>
            </AnimatePresence>

            <button
              onClick={handleNextMonth}
              aria-label="Mois suivant"
              className="clock-calendar__navigation-button p-1 flex items-center justify-center rounded-full hover:bg-surface-muted transition-colors text-primary"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>

          <div className="clock-calendar__calendar-surface">
            <div className="clock-calendar__days-grid">
              {daysOfWeek.map((day, index) => (
                <div className="clock-calendar__weekday" key={`${day}-${index}`}>
                  {day}
                </div>
              ))}

              {Array.from({ length: leadingEmptyDays }, (_, index) => (
                <div key={`empty-${index}`} aria-hidden="true" />
              ))}

              <AnimatePresence initial={false}>
                {monthDays.map((day) => {
                  const dayKey = format(day, "yyyy-MM-dd");
                  const isToday = dayKey === today;

                  return (
                    <motion.div
                      key={dayKey}
                      initial={{ opacity: 0, y: -5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 5 }}
                      transition={{ duration: 0.3 }}
                      className="clock-calendar__day-cell"
                    >
                      <span
                        className={`clock-calendar__day${
                          isToday ? " clock-calendar__day--today" : ""
                        }`}
                      >
                        {format(day, "d")}
                      </span>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>
          </div>
        </section>
      </div>

      <section className="clock-calendar__world-clocks" aria-label="Horaires internationaux">
        <FlipClock city="New York" time={getTimeInZone(-5)} />
        <FlipClock city="Dubaï" time={getTimeInZone(4)} />
        <FlipClock city="Singapour" time={getTimeInZone(8)} />
      </section>
    </div>
  );
};

export default ClockCalendar;
